import { addDays, isSameDay, startOfDay } from "date-fns";
import { estaAtrasado, prazoEfetivo, type PrazoDoLaboratorio, type ProsthesisStatus } from "@bookdental/schemas";
import { TERMINAL_STATUSES } from "@/components/prosthesis/prosthesis-meta";
import type { IncomingOrderRow, ServiceRow } from "@/components/production/production-types";

/** Pedidos recebidos (visão do laboratório) que caem num dia específico, mais antigo primeiro. */
export function ordersOnDay(orders: IncomingOrderRow[], day: Date): IncomingOrderRow[] {
  return orders
    .filter((o) => isSameDay(new Date(o.createdAt), day))
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

/** Atrasado = prazo explícito vencido OU (sem prazo) criação + prazo padrão do laboratório já
 *  passou — contado em dias úteis ou corridos, como o laboratório configurou. A regra mora em
 *  `estaAtrasado` (@bookdental/schemas), a mesma do painel das organizações. */
export function isOverdue(order: IncomingOrderRow, prazo: PrazoDoLaboratorio): boolean {
  return estaAtrasado(order, prazo);
}

/** Pedido próprio da clínica (resumo de pedidos do Início) — mesma forma devolvida por
 *  `prosthesis.listMineOutgoingDeadlineSummary`. */
export type ClinicOutgoingOrder = {
  id: string;
  patientId: string | null;
  patientName: string;
  status: ProsthesisStatus;
  deadline: Date | string | null;
  createdAt: Date | string;
  labName: string | null;
  numero: number | null;
  dentistName: string | null;
  services: { teeth: string | null; serviceCategory: { label: string } }[];
  prazo: PrazoDoLaboratorio;
};

export type DeadlineBucket = "atrasado" | "hoje" | "proximos7" | "proximos30";

/** Forma mínima pra calcular prazo/bucket — `IncomingOrderRow` satisfaz isso de sobra, mas
 *  qualquer pedido (inclusive o resumo de ENVIADOS da clínica, issue #14) também serve, sem
 *  precisar de mais uma cópia da mesma lógica de janela rolante. */
type DeadlineOrderLike = { deadline: Date | string | null; createdAt: Date | string; status: ProsthesisStatus };

/** Prazo efetivo do pedido: `deadline` explícito, ou criação + prazo padrão do laboratório. */
export function effectiveDeadline(order: DeadlineOrderLike, prazo: PrazoDoLaboratorio): Date {
  return prazoEfetivo(order, prazo);
}

/** Mesmo bucket, mas no nível do serviço — usado quando o resumo mostra 1 card por serviço em
 *  vez de 1 por pedido (um pedido com 2 serviços em fases diferentes cai em buckets diferentes). */
export function serviceDeadlineBucket(row: ServiceRow, prazo: PrazoDoLaboratorio): DeadlineBucket | null {
  if (!row.service) return null;
  if (row.service.status === "AGUARDANDO_CONFIRMACAO" || TERMINAL_STATUSES.includes(row.service.status)) {
    return null;
  }
  const today = startOfDay(new Date());
  const eff = effectiveDeadline(row.order, prazo);
  if (eff < today) return "atrasado";
  if (isSameDay(eff, today)) return "hoje";
  if (eff <= addDays(today, 7)) return "proximos7";
  if (eff <= addDays(today, 30)) return "proximos30";
  return null;
}
